/**
 * map_heat.ts — the World Atlas HEAT layers (people, economy): pure binning
 * and palettes, plus the one DOM draw the module calls.
 *
 * The technique is simpleheat's (Leaflet.heat): every point is a soft alpha
 * blob on an offscreen canvas — alpha = its share of the strongest point —
 * and ONE pass maps alpha → a 256-entry palette. Cost is O(points), any
 * zoom, no world sampling; the radius is in WORLD metres (a town's warmth
 * covers its ground, and zooming in spreads it) with a screen-pixel floor so
 * a hamlet at continental zoom is still a dot of colour.
 *
 * Points are BINNED first (a world grid whose cell follows the zoom): the
 * versona table is ~12k rows, and twelve thousand radial gradients a frame
 * is not free — a few hundred cells are.
 */

export interface HeatPoint { x: number; z: number; v: number }

/** Bin points onto a `cellM` world grid (v summed; the cell's centre is the point). */
export function binHeat(points: Iterable<HeatPoint>, cellM: number): HeatPoint[] {
  const m = new Map<string, HeatPoint>();
  for (const p of points) {
    if (!(p.v > 0) || !Number.isFinite(p.x) || !Number.isFinite(p.z)) continue;
    const cx = Math.floor(p.x / cellM), cz = Math.floor(p.z / cellM);
    const k = `${cx},${cz}`;
    const b = m.get(k);
    if (b) b.v += p.v; else m.set(k, { x: (cx + 0.5) * cellM, z: (cz + 0.5) * cellM, v: p.v });
  }
  return [...m.values()];
}

/** The bin size for a zoom: ~6 screen px, never under 24 m (a plaza), so bins are sub-blob. */
export function heatCellM(screenMpp: number): number { return Math.max(24, 6 * screenMpp); }

/** Perceptual scale for a value: sqrt keeps a 600-soul capital from washing out every hamlet. */
export function heatWeight(v: number, max: number): number {
  if (!(max > 0) || !(v > 0)) return 0;
  return Math.min(1, Math.sqrt(v / max));
}

/** A 256 × rgba palette from colour stops ([0..1] → [r,g,b]); alpha ramps 0 → 1 over the first stop. */
export function heatPalette(stops: Array<[number, [number, number, number]]>, alphaMax = 200): Uint8ClampedArray {
  const out = new Uint8ClampedArray(256 * 4);
  const s = [...stops].sort((a, b) => a[0] - b[0]);
  for (let i = 0; i < 256; i++) {
    const f = i / 255;
    let j = 1;
    while (j < s.length - 1 && s[j][0] < f) j++;
    const [f0, c0] = s[j - 1], [f1, c1] = s[j];
    const t = f1 > f0 ? Math.max(0, Math.min(1, (f - f0) / (f1 - f0))) : 0;
    out[i * 4] = c0[0] + (c1[0] - c0[0]) * t;
    out[i * 4 + 1] = c0[1] + (c1[1] - c0[1]) * t;
    out[i * 4 + 2] = c0[2] + (c1[2] - c0[2]) * t;
    out[i * 4 + 3] = Math.round(alphaMax * Math.min(1, f / Math.max(0.04, s[0][0] || 0.06)));
  }
  return out;
}

/** The classic heat ramp (blue → cyan → lime → yellow → red) — the people layer. */
export const PEOPLE_PALETTE = heatPalette([[0.06, [40, 70, 200]], [0.3, [50, 200, 230]], [0.5, [120, 240, 110]], [0.72, [255, 230, 60]], [1, [255, 60, 40]]], 210);
/** Gold for credits, teal for resources — the economy layer's two channels (composited 'lighter'). */
export const CREDITS_PALETTE = heatPalette([[0.06, [90, 60, 10]], [0.45, [220, 160, 40]], [1, [255, 235, 150]]], 190);
export const RESOURCES_PALETTE = heatPalette([[0.06, [10, 60, 70]], [0.45, [40, 190, 180]], [1, [170, 255, 240]]], 190);

/** Colorize an alpha canvas in place: every pixel's alpha picks a palette entry (simpleheat). */
export function colorizeAlpha(px: Uint8ClampedArray, palette: Uint8ClampedArray): void {
  for (let i = 0; i < px.length; i += 4) {
    const a = px[i + 3];
    if (a === 0) continue;
    const o = a * 4;
    px[i] = palette[o]; px[i + 1] = palette[o + 1]; px[i + 2] = palette[o + 2]; px[i + 3] = palette[o + 3];
  }
}

// ── DOM: the draw ──────────────────────────────────────────────────────────
const blobCache = new Map<number, HTMLCanvasElement>();
/** A soft radial blob of radius r px (cached per integer radius). */
function blob(r: number): HTMLCanvasElement {
  const key = Math.max(2, Math.round(r));
  let c = blobCache.get(key);
  if (c) return c;
  c = document.createElement('canvas');
  c.width = c.height = key * 2;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(key, key, 0, key, key, key);
  grad.addColorStop(0, 'rgba(0,0,0,1)');
  grad.addColorStop(0.55, 'rgba(0,0,0,0.45)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, key * 2, key * 2);
  if (blobCache.size > 64) blobCache.clear();
  blobCache.set(key, c);
  return c;
}

export interface HeatDrawOpts {
  /** World radius (m) of a full-strength point and the screen floor (px). */
  radiusM: number; minPx: number; maxPx: number;
  palette: Uint8ClampedArray;
  /** Max value for the sqrt scale (defaults to the strongest point). */
  max?: number;
}

let scratch: HTMLCanvasElement | null = null;
/**
 * Draw the heat of `points` over the map: blobs into an alpha scratch, one
 * colorize pass, then composite. `toScreen` maps world → canvas px.
 */
export function drawHeat(g: CanvasRenderingContext2D, points: HeatPoint[], W: number, H: number, scale: number,
  toScreen: (x: number, z: number) => [number, number], opts: HeatDrawOpts, composite: GlobalCompositeOperation = 'source-over'): number {
  if (points.length === 0 || W < 2 || H < 2) return 0;
  const max = opts.max ?? points.reduce((m, p) => Math.max(m, p.v), 0);
  if (!(max > 0)) return 0;
  scratch ??= document.createElement('canvas');
  if (scratch.width !== W || scratch.height !== H) { scratch.width = W; scratch.height = H; }
  const sg = scratch.getContext('2d', { willReadFrequently: true })!;
  sg.clearRect(0, 0, W, H);
  const rPx = Math.max(opts.minPx, Math.min(opts.maxPx, opts.radiusM * scale));
  const b = blob(rPx);
  let drawn = 0;
  for (const p of points) {
    const w = heatWeight(p.v, max);
    if (w < 0.02) continue;
    const [x, y] = toScreen(p.x, p.z);
    const r = rPx * (0.55 + 0.45 * w); // a strong point spreads a little wider
    if (x < -r || y < -r || x > W + r || y > H + r) continue;
    sg.globalAlpha = w;
    sg.drawImage(b, x - r, y - r, r * 2, r * 2);
    drawn++;
  }
  if (drawn === 0) return 0;
  const img = sg.getImageData(0, 0, W, H);
  colorizeAlpha(img.data, opts.palette);
  sg.putImageData(img, 0, 0);
  const prev = g.globalCompositeOperation;
  g.globalCompositeOperation = composite;
  g.drawImage(scratch, 0, 0);
  g.globalCompositeOperation = prev;
  return drawn;
}
